import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Sparkles, Plus, FolderOpen, Clock, BarChart3, FileText, LogOut, 
  User, Search, Trash2, ChevronRight, Home, Video, CreditCard, Calendar, AlertTriangle, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, fetchUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [generatingAim, setGeneratingAim] = useState(false);
  const [generatingObjectives, setGeneratingObjectives] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    research_aim: '',
    research_objectives: '',
    research_questions: '',
    research_type: 'quantitative',
    target_population: '',
    sample_size: '',
    quant_sample_size: '',
    qual_sample_size: ''
  });

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch subscription status');
    }
  }, [token]);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const [youtubeChannelUrl, setYoutubeChannelUrl] = useState('');
  
  const fetchYoutubeChannel = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/youtube-channel`);
      setYoutubeChannelUrl(res.data.url || '');
    } catch (err) {
      console.log('No YouTube channel configured');
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchSubscriptionStatus();
    fetchYoutubeChannel();
  }, [fetchProjects, fetchSubscriptionStatus, fetchYoutubeChannel]);

  const handleGenerateAim = async () => {
    if (!newProject.title.trim()) {
      toast.error('Please enter a title first');
      return;
    }
    setGeneratingAim(true);
    try {
      const res = await axios.post(`${API}/ai/generate-aim`, 
        { title: newProject.title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewProject(prev => ({ ...prev, research_aim: res.data.aim }));
      toast.success('Aim generated! You can edit it as needed.');
    } catch (err) {
      toast.error('Failed to generate aim');
    } finally {
      setGeneratingAim(false);
    }
  };

  const handleGenerateObjectives = async () => {
    if (!newProject.title.trim() || !newProject.research_aim.trim()) {
      toast.error('Please enter title and aim first');
      return;
    }
    setGeneratingObjectives(true);
    try {
      const res = await axios.post(`${API}/ai/generate-objectives`, 
        { 
          title: newProject.title, 
          aim: newProject.research_aim,
          research_type: newProject.research_type || 'quantitative'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewProject(prev => ({ ...prev, research_objectives: res.data.objectives }));
      toast.success('Objectives generated! You can edit them as needed.');
    } catch (err) {
      toast.error('Failed to generate objectives');
    } finally {
      setGeneratingObjectives(false);
    }
  };

  const isSubscriptionActive = subscriptionStatus?.status === 'active';

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.title || !newProject.research_aim) {
      toast.error('Title and Research Aim are required');
      return;
    }

    setCreating(true);
    try {
      const projectData = {
        ...newProject,
        sample_size: newProject.sample_size ? parseInt(newProject.sample_size) : null,
        quant_sample_size: newProject.quant_sample_size ? parseInt(newProject.quant_sample_size) : null,
        qual_sample_size: newProject.qual_sample_size ? parseInt(newProject.qual_sample_size) : null
      };
      const res = await axios.post(`${API}/projects`, projectData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects([res.data, ...projects]);
      setCreateDialogOpen(false);
      setNewProject({
        title: '',
        research_aim: '',
        research_objectives: '',
        research_questions: '',
        research_type: 'quantitative',
        target_population: '',
        sample_size: '',
        quant_sample_size: '',
        qual_sample_size: ''
      });
      toast.success('Project created!');
      navigate(`/project/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success('Project deleted');
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const filteredProjects = projects.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.research_aim.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeIcon = (type) => {
    switch (type) {
      case 'quantitative': return <BarChart3 className="w-4 h-4" />;
      case 'qualitative': return <FileText className="w-4 h-4" />;
      default: return <FolderOpen className="w-4 h-4" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      quantitative: 'bg-blue-100 text-blue-700',
      qualitative: 'bg-purple-100 text-purple-700',
      mixed: 'bg-green-100 text-green-700'
    };
    return styles[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-slate-300 p-6 hidden lg:block">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-serif font-bold text-white">StatsXY</span>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white"
            data-testid="nav-dashboard"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          {youtubeChannelUrl && (
            <a 
              href={youtubeChannelUrl} 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              data-testid="nav-tutorials"
            >
              <Video className="w-5 h-5" />
              Tutorials
            </a>
          )}
        </nav>

        {/* Access Info */}
        <div className="mt-6 p-4 bg-slate-800 rounded-xl" data-testid="access-info-panel">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-medium text-white">Access</span>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Status</span>
              <span className={`px-2 py-0.5 rounded text-xs ${
                isSubscriptionActive ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
              }`} data-testid="access-status-badge">
                {isSubscriptionActive ? 'Active' : 'Expired'}
              </span>
            </div>
            {subscriptionStatus?.start_date && isSubscriptionActive && (
              <div className="flex justify-between items-center">
                <span className="text-slate-400">From</span>
                <span className="text-slate-300 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(subscriptionStatus.start_date).toLocaleDateString()}
                </span>
              </div>
            )}
            {subscriptionStatus?.end_date && isSubscriptionActive && (
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Until</span>
                <span className="text-slate-300 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(subscriptionStatus.end_date).toLocaleDateString()}
                </span>
              </div>
            )}
            {subscriptionStatus?.price && isSubscriptionActive && (
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Plan</span>
                <span className="text-slate-300">${subscriptionStatus.price} / {Math.round(subscriptionStatus.duration_days / 30)} months</span>
              </div>
            )}
            {!isSubscriptionActive && (
              <Button
                onClick={() => navigate('/payment')}
                className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700"
                size="sm"
                data-testid="subscribe-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Purchase Access
              </Button>
            )}
          </div>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 bg-slate-800 rounded-xl mb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-medium">
                {user?.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="text-xs text-slate-400">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${
                user?.subscription_status === 'active' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
              }`}>
                {user?.subscription_status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <button 
            onClick={logout}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors w-full px-4 py-2"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-700 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif font-bold text-slate-900">StatsXY</span>
          </div>
          <div className="flex items-center gap-4">
            <button className="p-2 text-slate-600 hover:text-indigo-700">
              <User className="w-5 h-5" />
            </button>
            <button onClick={logout} className="p-2 text-slate-600 hover:text-indigo-700">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-20 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-10">
          {/* Expired Access Warning */}
          {!isSubscriptionActive && subscriptionStatus !== null && (
            <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-2xl" data-testid="access-expired-warning">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-yellow-800">Access Expired</h3>
                  <p className="text-yellow-700 mt-1">
                    Your access period has ended. Purchase a new {subscriptionStatus?.duration_days ? `${Math.round(subscriptionStatus.duration_days / 30)} month` : '3 month'} access to continue using your projects and analysis tools.
                  </p>
                  <Button
                    onClick={() => navigate('/payment')}
                    className="mt-4 bg-yellow-600 hover:bg-yellow-700 text-white"
                    data-testid="resubscribe-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Renew Access {subscriptionStatus?.price ? `- $${subscriptionStatus.price}` : ''}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-serif font-bold text-slate-900">Your Projects</h1>
              <p className="text-slate-600 mt-1">Manage and analyze your research data</p>
            </div>
            
            {isSubscriptionActive && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-project-btn" disabled={projects.length >= 2}>
                    <Plus className="w-5 h-5 mr-2" />
                    {projects.length >= 2 ? 'Project Limit Reached' : 'New Project'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">Create New Project</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-500">You can create up to 2 projects — quantitative, qualitative, or mixed methods.</p>
                  <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="title">Research Title *</Label>
                      <Input
                        id="title"
                        value={newProject.title}
                        onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                        className="input-field mt-1"
                        placeholder="e.g., Impact of Social Media on Student Performance"
                        data-testid="project-title-input"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="aim">Research Aim *</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleGenerateAim}
                          disabled={generatingAim || !newProject.title.trim()}
                          className="text-indigo-600 hover:text-indigo-700 h-7 px-2"
                          data-testid="generate-aim-btn"
                        >
                          {generatingAim ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                          ) : (
                            <><Sparkles className="w-3 h-3 mr-1" /> Generate</>
                          )}
                        </Button>
                      </div>
                      <Textarea
                        id="aim"
                        value={newProject.research_aim}
                        onChange={(e) => setNewProject({...newProject, research_aim: e.target.value})}
                        className="input-field mt-1"
                        placeholder="The aim of this research is to..."
                        rows={3}
                        data-testid="project-aim-input"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="objectives">Research Objectives</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleGenerateObjectives}
                          disabled={generatingObjectives || !newProject.title.trim() || !newProject.research_aim.trim()}
                          className="text-indigo-600 hover:text-indigo-700 h-7 px-2"
                          data-testid="generate-objectives-btn"
                        >
                          {generatingObjectives ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                          ) : (
                            <><Sparkles className="w-3 h-3 mr-1" /> Generate</>
                          )}
                        </Button>
                      </div>
                      <Textarea
                        id="objectives"
                        value={newProject.research_objectives}
                        onChange={(e) => setNewProject({...newProject, research_objectives: e.target.value})}
                        className="input-field mt-1"
                        placeholder="1. To assess...&#10;2. To identify...&#10;3. To examine..."
                        rows={4}
                        data-testid="project-objectives-input"
                      />
                    </div>

                  <div>
                    <Label htmlFor="type">Research Type</Label>
                    <Select 
                      value={newProject.research_type} 
                      onValueChange={(v) => setNewProject({...newProject, research_type: v})}
                    >
                      <SelectTrigger className="input-field mt-1" data-testid="project-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quantitative">Quantitative</SelectItem>
                        <SelectItem value="qualitative">Qualitative</SelectItem>
                        <SelectItem value="mixed">Mixed Methods</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {newProject.research_type !== 'qualitative' && (
                      <div>
                        <Label htmlFor="population">Target Population</Label>
                        <Input
                          id="population"
                          value={newProject.target_population}
                          onChange={(e) => setNewProject({...newProject, target_population: e.target.value})}
                          className="input-field mt-1"
                          placeholder="e.g., University students"
                          data-testid="project-population-input"
                        />
                      </div>
                    )}
                    {newProject.research_type === 'quantitative' && (
                      <div>
                        <Label htmlFor="sample">Sample Size</Label>
                        <Input
                          id="sample"
                          type="number"
                          value={newProject.sample_size}
                          onChange={(e) => setNewProject({...newProject, sample_size: e.target.value})}
                          className="input-field mt-1"
                          placeholder="e.g., 200"
                          data-testid="project-sample-input"
                        />
                      </div>
                    )}
                    {newProject.research_type === 'mixed' && (
                      <>
                        <div>
                          <Label htmlFor="quant_sample">Quantitative Sample</Label>
                          <Input
                            id="quant_sample"
                            type="number"
                            value={newProject.quant_sample_size}
                            onChange={(e) => setNewProject({...newProject, quant_sample_size: e.target.value})}
                            className="input-field mt-1"
                            placeholder="e.g., 200"
                            data-testid="project-quant-sample-input"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {(newProject.research_type === 'mixed' || newProject.research_type === 'qualitative') && (
                    <div>
                      <Label htmlFor="qual_sample">Qualitative Participants</Label>
                      <Input
                        id="qual_sample"
                        type="number"
                        value={newProject.qual_sample_size}
                        onChange={(e) => setNewProject({...newProject, qual_sample_size: e.target.value})}
                        className="input-field mt-1"
                        placeholder="e.g., 15 interview participants"
                        data-testid="project-qual-sample-input"
                      />
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setCreateDialogOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="btn-primary flex-1" 
                      disabled={creating}
                      data-testid="create-project-submit"
                    >
                      {creating ? 'Creating...' : 'Create Project'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>

          {/* Search - only show if subscription is active */}
          {isSubscriptionActive && (
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="search"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-10"
                data-testid="search-projects"
              />
            </div>
          )}

          {/* Projects Grid - only show if subscription is active */}
          {isSubscriptionActive ? (
            loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="card-dashboard text-center py-16" data-testid="empty-state">
                <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-serif font-semibold text-slate-900 mb-2">
                  {searchQuery ? 'No projects found' : 'No projects yet'}
                </h3>
                <p className="text-slate-600 mb-6">
                  {searchQuery ? 'Try a different search term' : 'Create your first research project to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Project
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                  <Link 
                    key={project.id} 
                    to={`/project/${project.id}`}
                    className="card-dashboard group hover:border-indigo-200 hover:shadow-md transition-all"
                    data-testid={`project-card-${project.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getTypeBadge(project.research_type)}`}>
                        {getTypeIcon(project.research_type)}
                        {project.research_type}
                      </span>
                  </div>
                  
                  <h3 className="text-lg font-serif font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-indigo-700 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">{project.research_aim}</p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(project.created_at).toLocaleDateString()}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
            )
          ) : (
            /* Inactive subscription - show blurred projects or empty state */
            <div className="card-dashboard text-center py-16 bg-slate-50/50">
              <div className="relative">
                <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-yellow-100 rounded-full p-2">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-serif font-semibold text-slate-900 mb-2">
                Access Restricted
              </h3>
              <p className="text-slate-600 mb-6">
                Purchase access to use your projects and analysis tools
              </p>
              <Button onClick={() => navigate('/payment')} className="btn-primary">
                <CreditCard className="w-5 h-5 mr-2" />
                Purchase Access
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
